import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, XCircle, User, DollarSign, FileText, Loader2, Clock, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCancellationsReport } from "@/hooks/useCancellationsReport";
import { useOperationalPeriod, OperationalPeriod } from "@/hooks/useOperationalPeriod";
import { useRestaurant } from "@/hooks/useRestaurant";
import { exportCancellationsToPDF, exportCancellationsToExcel } from "@/lib/report-export";

export default function CancelamentosPage() {
  const { cancellations, totalCancellations, totalValue, isLoading, fetchCancellations } = useCancellationsReport();
  const { currentPeriod, getPeriodsHistory } = useOperationalPeriod();
  const { activeRestaurant } = useRestaurant();
  
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(format(today, "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(today, "yyyy-MM-dd"));
  const [filterMode, setFilterMode] = useState<"period" | "date">("period");
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [periodsHistory, setPeriodsHistory] = useState<OperationalPeriod[]>([]);

  // Carregar histórico de períodos
  useEffect(() => {
    const loadPeriods = async () => {
      const history = await getPeriodsHistory(30);
      setPeriodsHistory(history);
    };
    loadPeriods();
  }, [getPeriodsHistory]);

  // Quando período atual carregar, selecionar automaticamente
  useEffect(() => {
    if (currentPeriod && !selectedPeriodId) {
      setSelectedPeriodId(currentPeriod.id);
    }
  }, [currentPeriod, selectedPeriodId]);

  // Buscar cancelamentos quando período selecionado mudar ou ao carregar
  useEffect(() => {
    if (filterMode === "period" && selectedPeriodId) {
      fetchCancellations({ periodId: selectedPeriodId });
    } else if (filterMode === "date") {
      // Parsear data corretamente como local
      const [yearFrom, monthFrom, dayFrom] = dateFrom.split('-').map(Number);
      const [yearTo, monthTo, dayTo] = dateTo.split('-').map(Number);
      fetchCancellations({
        dateFrom: new Date(yearFrom, monthFrom - 1, dayFrom),
        dateTo: new Date(yearTo, monthTo - 1, dayTo),
      });
    }
  }, [selectedPeriodId, filterMode]);

  const handleFilter = () => {
    if (filterMode === "period" && selectedPeriodId) {
      fetchCancellations({ periodId: selectedPeriodId });
    } else {
      // Parsear data corretamente como local
      const [yearFrom, monthFrom, dayFrom] = dateFrom.split('-').map(Number);
      const [yearTo, monthTo, dayTo] = dateTo.split('-').map(Number);
      fetchCancellations({
        dateFrom: new Date(yearFrom, monthFrom - 1, dayFrom),
        dateTo: new Date(yearTo, monthTo - 1, dayTo),
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const formatPeriodLabel = (period: OperationalPeriod) => {
    const refDate = format(new Date(period.reference_date), "dd/MM/yyyy", { locale: ptBR });
    const status = period.status === "open" ? " (Atual)" : "";
    return `${refDate}${status}`;
  };

  const getDateRange = () => {
    if (filterMode === "period" && selectedPeriodId) {
      const period = periodsHistory.find(p => p.id === selectedPeriodId) || currentPeriod;
      if (period) {
        return format(new Date(period.reference_date), "dd/MM/yyyy", { locale: ptBR });
      }
    }
    return `${format(new Date(dateFrom + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR })} a ${format(new Date(dateTo + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR })}`;
  };

  const handleExportPDF = () => {
    exportCancellationsToPDF(cancellations as any, totalValue, getDateRange(), activeRestaurant?.name);
  };

  const handleExportExcel = () => {
    exportCancellationsToExcel(cancellations as any, totalValue, getDateRange());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Cancelamentos</h1>
          <p className="text-muted-foreground">
            Histórico de itens cancelados
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={cancellations.length === 0}>
            <Download className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={cancellations.length === 0}>
            <Download className="h-4 w-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={filterMode} onValueChange={(v) => setFilterMode(v as "period" | "date")}>
            <TabsList className="mb-4">
              <TabsTrigger value="period" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Por Período
              </TabsTrigger>
              <TabsTrigger value="date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Por Data
              </TabsTrigger>
            </TabsList>

            <TabsContent value="period" className="mt-0">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label>Período Operacional</Label>
                  <Select
                    value={selectedPeriodId || ""}
                    onValueChange={setSelectedPeriodId}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Selecione um período" />
                    </SelectTrigger>
                    <SelectContent>
                      {currentPeriod && (
                        <SelectItem value={currentPeriod.id}>
                          {formatPeriodLabel(currentPeriod)}
                        </SelectItem>
                      )}
                      {periodsHistory
                        .filter(p => p.id !== currentPeriod?.id)
                        .map((period) => (
                          <SelectItem key={period.id} value={period.id}>
                            {formatPeriodLabel(period)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleFilter} disabled={isLoading || !selectedPeriodId}>
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    "Atualizar"
                  )}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="date" className="mt-0">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="date-from">Data Inicial</Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date-to">Data Final</Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button onClick={handleFilter} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <Calendar className="h-4 w-4 mr-2" />
                      Filtrar
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Cancelamentos</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCancellations}</div>
            <p className="text-xs text-muted-foreground">itens cancelados no período</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Total Cancelado</CardTitle>
            <DollarSign className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{formatCurrency(totalValue)}</div>
            <p className="text-xs text-muted-foreground">em produtos cancelados</p>
          </CardContent>
        </Card>
      </div>

      {/* Cancellations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Detalhamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : cancellations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum cancelamento encontrado no período selecionado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Pedido</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead className="text-center">Qtd</TableHead>
                    <TableHead className="text-right">Valor Unit.</TableHead>
                    <TableHead className="text-right">Valor Total</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Lançado por</TableHead>
                    <TableHead>Cancelado por</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cancellations.map((cancellation) => (
                    <TableRow key={cancellation.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTime(cancellation.cancelled_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">#{cancellation.order?.order_number}</span>
                          {cancellation.order?.table_number && (
                            <span className="text-xs text-muted-foreground">
                              Mesa {cancellation.order.table_number}
                            </span>
                          )}
                          {cancellation.order?.tab_number && (
                            <span className="text-xs text-muted-foreground">
                              Comanda {cancellation.order.tab_number}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{cancellation.product_name}</span>
                          {cancellation.notes && (
                            <span className="text-xs text-muted-foreground">
                              Obs: {cancellation.notes}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{cancellation.quantity}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(Number(cancellation.unit_price))}
                      </TableCell>
                      <TableCell className="text-right font-medium text-destructive">
                        {formatCurrency(Number(cancellation.total_price))}
                      </TableCell>
                      <TableCell className="max-w-[150px]">
                        <span className="text-sm text-muted-foreground truncate block">
                          {cancellation.reason || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">
                            {cancellation.created_by_profile?.full_name || "N/A"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-destructive" />
                          <span className="text-sm">
                            {cancellation.cancelled_by_profile?.full_name || "N/A"}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
